import { useTranslation } from 'react-i18next';

import { SearchPriceInput } from '@/components/form/search-price-input';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

/** The resolver refuses anything sooner; mirror it here so the merchant is told up front. */
const MIN_LEAD_HOURS = 24;

const toDateInputValue = (date: Date): string => date.toISOString().slice(0, 10);

export const earliestAllowedDate = (): Date => new Date(Date.now() + (MIN_LEAD_HOURS + 1) * 60 * 60 * 1000);

interface ConfigureStepProps {
  storeId: string;
  orderCount: number;
  priceId?: string;
  firstChargeDate?: Date;
  reason: string;
  onPriceChange: (priceId: string) => void;
  onFirstChargeDateChange: (date: Date | undefined) => void;
  onReasonChange: (reason: string) => void;
}

export function ConfigureStep({
  storeId,
  orderCount,
  priceId,
  firstChargeDate,
  reason,
  onPriceChange,
  onFirstChargeDateChange,
  onReasonChange,
}: ConfigureStepProps) {
  const { t } = useTranslation('subscriptions');

  return (
    <div className="space-y-5">
      <p className="text-muted-foreground text-sm">
        {t('createFromOrders.configure.intro', { count: orderCount })}
      </p>

      <div className="space-y-2">
        <Label>{t('createFromOrders.configure.plan')}</Label>
        <SearchPriceInput
          storeId={storeId}
          priceId={priceId}
          recurringOnly
          onChange={(price) => onPriceChange(price.priceId)}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="create-sub-first-charge">
          {t('createFromOrders.configure.firstCharge')}
        </Label>
        <Input
          id="create-sub-first-charge"
          type="date"
          min={toDateInputValue(earliestAllowedDate())}
          value={firstChargeDate ? toDateInputValue(firstChargeDate) : ''}
          onChange={(e) =>
            onFirstChargeDateChange(e.target.value ? new Date(`${e.target.value}T12:00:00`) : undefined)
          }
        />
        <p className="text-muted-foreground text-xs">
          {t('createFromOrders.configure.firstChargeHint')}
        </p>
      </div>

      <p className="text-muted-foreground text-xs">
        {t('createFromOrders.configure.quantityNote')}
      </p>

      <div className="space-y-2">
        <Label htmlFor="create-sub-reason">
          {t('createFromOrders.configure.reason')}
        </Label>
        <Textarea
          id="create-sub-reason"
          value={reason}
          onChange={(e) => onReasonChange(e.target.value)}
          placeholder={t('createFromOrders.configure.reasonPlaceholder')}
        />
        <p className="text-muted-foreground text-xs">
          {t('createFromOrders.configure.reasonHint')}
        </p>
      </div>
    </div>
  );
}
